import { ReactComponent as BackArrow } from "../../../assets/arrow-back.svg";
import { ReactComponent as File } from "../../../assets/file-small.svg";
import { ReactComponent as Clock } from "../../../assets/clock.svg";
import Button from "../../../custom/button/button";
import classNames from "classnames";
import styles from "../styles.module.scss";
import Points from "../../../assets/points.png";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getAllBranches,
  getEventById,
  getEventScheduleByEventId,
  getMaterials,
  registerNonMemberEvent,
  validateStudentId,
} from "../../../requests";
import { App, Spin } from "antd";
import { AxiosError } from "axios";
import { useState } from "react";
import { getDateWithoutTime } from "../../../utils/formatDate";
import {
  ErrorMessage,
  Field,
  FormikProvider,
  FormikValues,
  useFormik,
} from "formik";
import { useAtom } from "jotai";
import { otherPayment } from "../../../state";
import Input from "../../../custom/input/input";
import PayForOther from "./payment";
import * as Yup from "yup";
import Upload from "../../../custom/upload/upload";
import Select from "../../../custom/select/select";
import MessageBox from "../../corporateMembers/eventApplication/conference/messageBox";

const ApplyForNonMemberOther = () => {
  const [params] = useSearchParams();
  const { notification } = App.useApp();
  const [idError, setIdError] = useState("");
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const [isEventScheduled, setIsEventScheduled] = useState(false);
  const [otherPaymentDetails, setOtherPaymentDetails] = useAtom(otherPayment);
  const navigate = useNavigate();
  const learningEventId = params.get("learningEventId");
  const isStudent = params.get("isStudent");

  const validateStudentMutation = useMutation({
    mutationFn: validateStudentId,
    retry: 0,
  });
  const registerEventMutation = useMutation({
    mutationKey: ["register-event"],
    mutationFn: registerNonMemberEvent,
  });
  const otherInfo = useQuery({
    queryKey: ["get-other-details"],
    queryFn: () => getEventById(parseInt(learningEventId!)),
    enabled: !!learningEventId,
  });
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["get-event-schedule"],
    queryFn: () => getEventScheduleByEventId(parseInt(learningEventId!)),
  });
  const materials = useQuery({
    queryKey: ["get-other-materials"],
    queryFn: () => getMaterials(learningEventId!),
  });
  const branches = useQuery({
    queryKey: ["get-branches"],
    queryFn: getAllBranches,
  });

  const validationSchema = Yup.object().shape({
    schedule: Yup.string().required("required"),
    name: Yup.string().required("required"),
    studentId: isStudent
      ? Yup.string().required("required")
      : Yup.string().notRequired(),
    phoneNumber: Yup.string()
      .required("required")
      .test(
        "no-spaces",
        "Phone number must not contain any spaces",
        (value) => !value?.includes(" ")
      )
      .matches(/^\+?\d+$/, "Invalid phone number"),
    email: Yup.string().email("Invalid email").required("required"),
    branch: Yup.number().min(1, "required").required("required"),
  });

  const formik = useFormik<FormikValues>({
    initialValues: {
      schedule: "",
      name: "",
      studentId: "",
      phoneNumber: "",
      email: "",
      branch: 0,
    },
    onSubmit: (values) => {
      if (!profilePic) {
        notification.error({
          message: "Please upload a profile picture",
        });
        return;
      } else registerEventHandler(values);
    },
    validationSchema,
  });

  const materialData = materials?.data?.Data as Material[];
  const otherInfoData = otherInfo?.data?.Data as EventById;
  const otherSchedule = data?.Data as EventSchedule[];
  const branchData = branches?.data?.Data;

  const branchOptions = branchData?.map((branch) => (
    <option key={branch.BranchId} value={branch.BranchId}>
      {branch.BranchName}
    </option>
  ));

  if (isLoading) return <Spin />;
  if (isError) return <div>{(error as AxiosError)?.message}</div>;

  const registerEventHandler = async (values: FormikValues) => {
    const payload = new FormData();
    payload.append("ParticipantName", values.name);
    payload.append("ParticipantPhoneNo", values.phoneNumber);
    payload.append("ParticipantEmail", values.email);
    payload.append("IsAMember", "false");
    payload.append("RequirePayment", `${otherInfoData?.IsPaymentRequired}`);
    payload.append("HasPaid", "false");
    payload.append("HasAttended", "false");
    payload.append("EventId", `${otherInfoData?.EventNameId}`);
    payload.append("TrainingscheduleId", `${parseInt(values.schedule)}`);
    payload.append("IsCorporateSponsor", "false");
    payload.append("EvenType", "Other");
    payload.append("EventCategoryId", `${otherInfoData?.EventCategoryId}`);
    payload.append("EventDetailId", `${otherInfoData?.LearningEventId}`);
    payload.append("BranchId", `${parseInt(values.branch)}`);
    if (profilePic) {
      payload.append("Picture", profilePic);
    }
    if (isStudent) {
      payload.append("isStudent", isStudent);
      payload.append("MembershipNo", values.studentId);
    }

    try {
      await registerEventMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          const dataAsObj = data?.Data as ProcessPaymentPayload;
          setOtherPaymentDetails({
            RouteId: dataAsObj?.RouteId,
            MembershipNo: dataAsObj?.MembershipNo,
            Amount: dataAsObj?.Amount,
            Description: dataAsObj?.Description,
            ReturnURL: dataAsObj?.ReturnURL,
            PaymentForm: dataAsObj?.PaymentForm,
            DataId: dataAsObj?.DataId,
            IsCorporateSponsor: formik.values.memberType === "corporate",
          });
          setIsEventScheduled(true);
          if (dataAsObj?.Amount === 0) {
            navigate("/non-member-events/other");
            notification.success({
              message: "Success",
              description: "Event registered successfully.",
            });
          }
        },
      });
    } catch (error: any) {
      notification.error({
        message:
          error?.Message ||
          error?.response?.data?.Message ||
          error?.response?.data?.message ||
          error?.message,
      });
      if (
        error?.response?.data?.Message?.includes(
          "The member has registered for the event"
        )
      ) {
        const dataAsObj = error?.response?.data?.Data as ProcessPaymentPayload;
        const dataAsNumber = error?.response?.data?.Data as number;
        setOtherPaymentDetails({
          RouteId: dataAsObj?.RouteId,
          MembershipNo: dataAsObj?.MembershipNo,
          Amount: dataAsObj?.Amount,
          Description: dataAsObj?.Description,
          ReturnURL: dataAsObj?.ReturnURL,
          PaymentForm: dataAsObj?.PaymentForm,
          DataId:
            formik.values.memberType === "corporate"
              ? dataAsNumber
              : dataAsObj?.DataId,
          IsCorporateSponsor: formik.values.memberType === "corporate",
        });
        //setIsEventScheduled(true);
      }
    }
  };

  const handleStudentValidation = async () => {
    try {
      await validateStudentMutation.mutateAsync(formik.values.studentId, {
        onSuccess: (data) => {
          formik.setFieldValue("name", data?.Data);
        },
      });
    } catch (error: any) {
      formik.setFieldValue("name", "");
      setIdError(error?.response?.data?.Message);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) setProfilePic(files[0]);
  };

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <section className={classNames("headingWithBackArrow")}>
          <button onClick={() => navigate(-1)}>
            <BackArrow />
          </button>
          <h3>Event Details</h3>
        </section>
        <section className={styles.formContainer}>
          <Upload
            label="Upload Profile Picture"
            accept="img"
            allowedFormats={["img"]}
            onChange={handleFileChange}
            fileName={profilePic?.name}
            fileSize={profilePic?.size}
          />
          {isStudent && (
            <div>
              <Field
                as={Input}
                name="studentId"
                label="Student ID"
                placeholder="Student ID"
                onBlur={(e: any) => {
                  setIdError("");
                  formik.handleBlur(e);
                  if (formik.values.studentId.trim()) {
                    handleStudentValidation();
                  }
                }}
              />
              {idError && <MessageBox message={idError} />}
            </div>
          )}
          <Field
            as={Input}
            name="name"
            label={
              validateStudentMutation.isPending
                ? "Fetching student name..."
                : "Name"
            }
            placeholder="Name"
            disabled={isStudent}
          />
          <div className={styles.fields}>
            <Field
              as={Input}
              name="phoneNumber"
              label="Phone Number"
              placeholder="Phone number"
            />
            <Field
              as={Input}
              name="email"
              label="Email Address"
              placeholder="Email address"
            />
          </div>
        </section>
        <section className={styles.detailsContainer}>
          <div className={styles.detailsContent}>
            <h2>
              {otherInfoData?.EventName}
              <span>
                <img src={Points} alt="points" />
              </span>
            </h2>
            <br />
            <div className={styles.line}></div>
            <br />
            {/* <h3>Venue</h3>
            <p>{otherInfoData?.Venue}</p>
            <br /> */}
            <h3>Business Case</h3>
            <p>{otherInfoData?.BusinessCase || "N/A"}</p>
            <br />
            <h3>Who to Participate</h3>
            <ul>
              <li>{otherInfoData?.TargetMember || "N/A"}</li>
            </ul>
            <br />
            <h3>Core Objectives</h3>
            <p>{otherInfoData?.CoreObjective || "N/A"}</p>
            <br />
            <h3>Talking Key Points</h3>
            <p>{otherInfoData?.DiscussionPoint || "N/A"}</p>
            <br />
            {materialData && materialData?.length > 0 ? (
              <h3>Event Schedule Material</h3>
            ) : null}
            <br />
            <div className={styles.list}>
              {materialData?.map((item, index) => (
                <div key={index} className={styles.files}>
                  <div>
                    <File />
                    <label>{item.MaterialName}</label>
                  </div>

                  <Link
                    to={item.MaterialUrl}
                    target="_blank"
                    className={classNames("linkAsBtn")}
                  >
                    Download
                  </Link>
                </div>
              ))}
            </div>
          </div>
          {isEventScheduled ? (
            <div className={styles.list}>
              {otherInfoData?.IsPaymentRequired && (
                <PayForOther
                  amount={otherPaymentDetails?.Amount}
                  email={formik.values.email}
                />
              )}
            </div>
          ) : (
            <div className={styles.scheduleContainer}>
              <h3>Select preferred schedule</h3>
              {otherSchedule?.map((item) => {
                const startDate = getDateWithoutTime(item.StartDate);
                const endDate = getDateWithoutTime(item.EndDate);
                return (
                  <section
                    key={item.LearningEventId}
                    className={styles.eventSchedule}
                  >
                    <Clock />
                    <div>
                      <h4>{item?.Venue}</h4>
                      <p>
                        {startDate} - {endDate}
                      </p>
                      <p>{item.Time}</p>
                    </div>
                    <Field
                      type="radio"
                      name="schedule"
                      value={`${item?.TrainingScheduleId}`}
                    />
                  </section>
                );
              })}
              <ErrorMessage name="schedule">
                {(msg) => <div className={styles.error}>{msg}</div>}
              </ErrorMessage>
              <br />
              <div className={styles.list}>
                {/* <Field
                  name="state"
                  as={Select}
                  label="State"
                  placeholder="State"
                  options={stateOptions}
                /> */}
                <Field
                  name="branch"
                  as={Select}
                  label="Location"
                  placeholder="Select location"
                  options={branchOptions}
                />
              </div>
              <br />
              <Button
                isLoading={registerEventMutation.isPending}
                disabled={registerEventMutation.isPending}
                type="submit"
                text={
                  otherInfoData?.IsPaymentRequired
                    ? "Proceed to Payment"
                    : "Register"
                }
              />
            </div>
          )}
        </section>
      </form>
    </FormikProvider>
  );
};

export default ApplyForNonMemberOther;
